import * as Dialog from '@radix-ui/react-dialog';
import { type ReactNode } from 'react';

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title is REQUIRED for a11y. Radix wires it to aria-labelledby. */
  title: string;
  /** Optional description, wired to aria-describedby by Radix. */
  description?: ReactNode;
  children: ReactNode;
  /** Footer slot — typically Cancel / Confirm buttons. */
  footer?: ReactNode;
  /**
   * Cap the modal width. Defaults to 'md' (28rem).
   * - 'sm' = 24rem  - 'md' = 28rem  - 'lg' = 32rem  - 'xl' = 36rem
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const sizes: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

/**
 * Modal — thin wrapper around Radix Dialog. Gets us for free:
 *   - Focus trap inside the dialog
 *   - ESC closes
 *   - Click outside closes (configurable via onOpenChange)
 *   - aria-labelledby and aria-describedby wired automatically
 *   - Portal rendering (no z-index hell)
 *
 * Renders nothing until `open` flips to true.
 *
 * @example
 *   const [open, setOpen] = useState(false);
 *   <Modal
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Confirmar pago"
 *     description="¿Procedés con el pago de COP 150.000?"
 *     footer={
 *       <>
 *         <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
 *         <Button onClick={pay}>Pagar</Button>
 *       </>
 *     }
 *   >
 *     Detalle de items, totales, etc.
 *   </Modal>
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content
          className={[
            'fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2',
            sizes[size],
            'rounded-card border border-fc-line bg-fc-paper p-6 shadow-xl',
            'focus:outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          ].join(' ')}
        >
          <Dialog.Title className="font-display text-2xl text-fc-ink">{title}</Dialog.Title>
          {description !== undefined && (
            <Dialog.Description className="mt-2 text-sm text-fc-muted">
              {description}
            </Dialog.Description>
          )}
          <div className="mt-4 text-fc-ink">{children}</div>
          {footer && (
            <div className="mt-6 flex flex-col gap-2 md:flex-row md:justify-end">{footer}</div>
          )}
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Cerrar"
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-card text-fc-muted hover:bg-fc-line/40 hover:text-fc-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fc-gold"
            >
              <span aria-hidden>×</span>
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
